import { MatDialog } from '@angular/material/dialog';
import { ShareToWorkspaceDialogComponent } from '../share-to-workspace/share-to-workspace-dialog.component';
import { Component, OnInit } from "@angular/core";
import { AppService } from "../app.service";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";

@Component({
  selector: "app-memories",
  standalone: false,
  templateUrl: "./memories.component.html",
  styleUrls: ["./memories.component.css"],
})
export class MemoriesComponent implements OnInit {
  displayedColumns: string[] = ["memory", "info", "tokens", "actions"];
  memlist: any = [];
  apiKey: any;
  userKey: any;
  showErrorMsg: boolean = false;
  showErrorText: any;
  status: any;
  csummery: any;
  workspaceName: any;
  txt: any;
  loading = true;

  // Tag filter properties
  selectedTag: string = "";
  isFilteredByTag: boolean = false;

  // Token filter properties
  selectedTokens: string[] = [];
  isFilteredByToken: boolean = false;

  // Popup properties
  showForgetPopup: boolean = false;
  selectedMemory: any = null;
  popupPosition: any = { top: 0, left: 0 };

  // NEW: Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 25;
  totalMemories: number = 0;
  totalPages: number = 0;
  jumpToPageNumber: number = 1;

  // NEW: Search and filter
  searchQuery: string = "";
  filteredMemories: any[] = [];
  paginatedMemories: any[] = [];

  // NEW: Sort
  sortBy: string = "date-desc";

  // NEW: View mode
  viewMode: 'grid' | 'list' | 'compact' = 'grid';

  constructor(
    public appService: AppService,
    public router: Router,
    private http: HttpClient,
    private route: ActivatedRoute,
  private dialog: MatDialog
  ) {
    this.userKey = this.route.snapshot.paramMap.get("key");
  }

  /**
   * Put this memory into one or more shared workspaces.
   *
   * The item does not move: XDB adds the workspace keys to the record that is
   * already here, and the connector resolves those keys from workspaces this
   * account owns. Nothing about the key travels through the browser.
   */
  shareMemory(event: Event, memory: any): void {
    event.stopPropagation();

    const workspaceId = this.currentWorkspaceId();
    if (!workspaceId) {
      this.appService.addWarnMessage(
        'Pick a workspace first - this page does not know which one you are in.');
      return;
    }

    this.dialog.open(ShareToWorkspaceDialogComponent, {
      width: '460px',
      data: {
        type: 'MEMORY',
        // Memories are identified by transactionNumber on this screen and
        // recipes by queryHash; XDB's shareMemory takes queryHash. Prefer the
        // real one and fall back rather than silently sending nothing.
        queryHash: memory?.queryHash || memory?.transactionNumber,
        sourceWorkspaceId: workspaceId,
        userKey: this.userKey,
        preview: memory?.memory,
      },
    });
  }

  /** Id of the workspace this screen is showing, from the stored selection. */
  private currentWorkspaceId(): number | null {
    try {
      const raw = this.appService.get('xdbc.workspace');
      if (!raw || typeof raw !== 'string') {
        return null;
      }
      const ws = JSON.parse(raw);
      return ws?.workspaceId || ws?.id || null;
    } catch {
      return null;
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.getConnectorSummary();
    
    // Load view mode from localStorage
    const savedViewMode = localStorage.getItem('memoriesViewMode');
    if (savedViewMode) {
      this.viewMode = savedViewMode as 'grid' | 'list' | 'compact';
    }

    // Load items per page from localStorage
    const savedItemsPerPage = localStorage.getItem('memoriesItemsPerPage');
    if (savedItemsPerPage) {
      this.itemsPerPage = parseInt(savedItemsPerPage, 10);
    }
    
    // Check for tag or token query parameters
    this.route.queryParams.subscribe(params => {
      this.selectedTag = params['tag'] || "";
      this.isFilteredByTag = !!this.selectedTag;
      
      const tokenParam = params['token'];
      if (tokenParam) {
        this.selectedTokens = [tokenParam];
        this.isFilteredByToken = true;
      } else {
        this.selectedTokens = [];
        this.isFilteredByToken = false;
      }
      
      if (this.isFilteredByTag) {
        this.getMemoriesByTag(this.selectedTag);
      } else if (this.isFilteredByToken) {
        this.getMemoriesByTokens(this.selectedTokens);
      } else {
        this.listMemories();
      }
    });
  }

  async getConnectorSummary() {
    try {
      let activationPayload: any = {
        routeType:"UI_SESSION"
      };
      
      const wsJson = this.appService.get("xdbc.workspace");

      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.apiKey = ws.apiKey;
          this.status = ws.status;
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      }  

      const signatureData = await this.appService.wssign(
        activationPayload,
        this.workspaceName
      );

      if (!signatureData) {
        throw new Error("Failed to sign activation payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        activationPayload,
        "usage/connector-summary",
        this.apiKey,
        signature
      );

      if (response && response.status == "Success") {
        this.txt = response.message;
      }
    } catch (error: any) {
      this.txt = "Activation failed: " + (error.message || "Unknown error");
    }
  }

  formatTimeStamp(ts: number): string {
    const timestamp = ts * 1000;
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  async copyMessage(message: string) {
    try {
      await navigator.clipboard.writeText(message);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }

  showForgetMemoryPopup(event: MouseEvent, memory: any) {
    event.stopPropagation();

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.popupPosition = {
      top: rect.top + window.scrollY - 10,
      left: rect.left + window.scrollX - 200,
    };

    this.selectedMemory = memory;
    this.showForgetPopup = true;
  }

  closeForgetPopup() {
    this.showForgetPopup = false;
    this.selectedMemory = null;
  }

  async forgetMemory() {
    try {
      if (!this.selectedMemory) return;

      const wsJson = this.appService.get("xdbc.workspace");

      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.apiKey = ws.apiKey;
          this.workspaceName = ws.workspaceName;
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      }  

      const forgetPayload = {
        userKey: this.selectedMemory.userkey,
        transactionNumber: this.selectedMemory.transactionNumber,
        routeType:"UI_SESSION"
      };

      const signatureData = await this.appService.wssign(
        forgetPayload,
        this.workspaceName
      );

      if (!signatureData) {
        throw new Error("Failed to sign forget payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        forgetPayload,
        "memory/forget",
        this.apiKey,
        signature
      );

      if (response && response.status == "Success") {
        this.memlist = this.memlist.filter(
          (mem: any) =>
            mem.transactionNumber !== this.selectedMemory.transactionNumber
        );
        this.txt = "Memory forgotten successfully";
        this.updatePagination();
      } else {
        throw new Error(response?.message || "Failed to forget memory");
      }
    } catch (error: any) {
      this.txt = "Failed to forget memory: " + (error.message || "Unknown error");
    } finally {
      this.closeForgetPopup();
    }
  }

  async listMemories() {
    try {
      const wsJson = this.appService.get("xdbc.workspace");
      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.apiKey = ws.apiKey;
          this.status = ws.status;
          this.workspaceName = ws.workspaceName;
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      } 

      if (this.status === "ACTIVATED") {
        this.showErrorMsg = false;
      } else {
        this.showErrorMsg = true;
        this.showErrorText = "The selected workspace is currently inactive. Please activate it to proceed.";
      }

      const activationPayload = this.userKey
        ? { userKey: this.userKey, tokens: [], query: "",routeType:"UI_SESSION" }
        : { tokens: [], query: "",routeType:"UI_SESSION" };

      const signatureData = await this.appService.wssign(
        activationPayload,
        this.workspaceName
      );

      if (!signatureData) {
        throw new Error("Failed to sign activation payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        activationPayload,
        "memory/list",
        this.apiKey,
        signature
      );
      
      this.loading = false;
      if (response && response.status == "Success") {
        this.memlist = response?.data?.memories || [];
        this.totalMemories = this.memlist.length;
        this.updatePagination();
        this.txt = response.message;
      } else {
        throw new Error(response?.message);
      }
    } catch (error: any) {
      this.loading = false;
      this.txt = "Activation failed: " + (error.message || "Unknown error");
    }
  }

  async getMemoriesByTag(tag: string) {
    try {
      const wsJson = this.appService.get("xdbc.workspace");
      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.apiKey = ws.apiKey;
          this.status = ws.status;
          this.workspaceName = ws.workspaceName;
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      }

      if (this.status === "ACTIVATED") {
        this.showErrorMsg = false;
      } else {
        this.showErrorMsg = true;
        this.showErrorText = "The selected workspace is currently inactive. Please activate it to proceed.";
        this.loading = false;
        return;
      }

      const payload = {
        userKey: this.userKey,
        tag: [tag],
        routeType: "UI_SESSION"
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        "memory/by-tag",
        this.apiKey,
        signature
      );

      this.loading = false;
      if (response && response.status == "Success") {
        this.memlist = response?.data?.memories || [];
        this.totalMemories = this.memlist.length;
        this.updatePagination();
        this.txt = response.message;
      } else {
        throw new Error(response?.message || "Failed to fetch memories by tag");
      }
    } catch (error: any) {
      this.loading = false;
      this.txt = "Failed to load memories: " + (error.message || "Unknown error");
    }
  }

  async getMemoriesByTokens(tokens: string[]) {
    try {
      const wsJson = this.appService.get("xdbc.workspace");
      if (wsJson !== undefined && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
        try {
          const ws = JSON.parse(wsJson);
          this.apiKey = ws.apiKey;
          this.status = ws.status;
          this.workspaceName = ws.workspaceName;
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      }

      if (this.status === "ACTIVATED") {
        this.showErrorMsg = false;
      } else {
        this.showErrorMsg = true;
        this.showErrorText = "The selected workspace is currently inactive. Please activate it to proceed.";
        this.loading = false;
        return;
      }

      const payload = {
        userKey: this.userKey,
        tokens: tokens,
        query: "",
        routeType: "UI_SESSION"
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        "memory/list",
        this.apiKey,
        signature
      );

      this.loading = false;
      if (response && response.status == "Success") {
        this.memlist = response?.data?.memories || [];
        this.totalMemories = this.memlist.length;
        this.updatePagination();
        this.txt = response.message;
      } else {
        throw new Error(response?.message || "Failed to fetch memories by tokens");
      }
    } catch (error: any) {
      this.loading = false;
      this.txt = "Failed to load memories: " + (error.message || "Unknown error");
    }
  }

  filterByToken(token: string) {
    if (token) {
      this.router.navigate(["/memories", this.userKey], {
        queryParams: { token: token }
      });
    }
  }

  filterByTag(tag: string) {
    if (tag) {
      this.router.navigate(["/memories", this.userKey], {
        queryParams: { tag: tag }
      });
    }
  }

  clearFilter() {
    this.router.navigate(["/memories", this.userKey]);
  }

  // NEW: Pagination methods
  /**
   * Which memories to show: everything you can read, only what you wrote, or
   * only what reached you through a shared workspace.
   *
   * Recall now spans both, so the list mixes your own memories with other
   * people's. `isShared` is set by the server on each record - it means
   * somebody else wrote it, not merely that it is visible through sharing, so
   * a memory you wrote and then shared out still counts as yours.
   */
  ownerFilter: 'ALL' | 'MINE' | 'SHARED' = 'ALL';

  setOwnerFilter(value: 'ALL' | 'MINE' | 'SHARED') {
    this.ownerFilter = value;
    this.currentPage = 1;
    this.updatePagination();
  }

  /** How many of each, before the search box narrows anything. */
  get sharedCount(): number {
    return this.memlist.filter((m: any) => m?.isShared).length;
  }

  get ownCount(): number {
    return this.memlist.length - this.sharedCount;
  }

  updatePagination() {
    // Apply search filter
    this.filteredMemories = this.memlist.filter((memory: any) => {
      // Ownership first: a record with no isShared could not be classified by
      // the server, so it stays in "mine" rather than being hidden from both.
      if (this.ownerFilter === 'SHARED' && !memory?.isShared) return false;
      if (this.ownerFilter === 'MINE' && memory?.isShared) return false;
      if (!this.searchQuery) return true;
      const query = this.searchQuery.toLowerCase();
      return (
        memory.memory?.toLowerCase().includes(query) ||
        memory.tokens?.some((token: string) => token.toLowerCase().includes(query)) ||
        memory.transactionNumber?.toLowerCase().includes(query)
      );
    });

    // Apply sorting
    this.applySorting();

    // Calculate pagination
    this.totalPages = Math.ceil(this.filteredMemories.length / this.itemsPerPage);
    
    // Reset to page 1 if current page is out of range
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }

    // Get paginated slice
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedMemories = this.filteredMemories.slice(startIndex, endIndex);
  }

  applySorting() {
    switch(this.sortBy) {
      case 'date-desc':
        this.filteredMemories.sort((a, b) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        break;
      case 'date-asc':
        this.filteredMemories.sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });
        break;
      case 'keywords-desc':
        this.filteredMemories.sort((a, b) => {
          return (b.tokens?.length || 0) - (a.tokens?.length || 0);
        });
        break;
      case 'keywords-asc':
        this.filteredMemories.sort((a, b) => {
          return (a.tokens?.length || 0) - (b.tokens?.length || 0);
        });
        break;
    }
  }

  onSearchChange() {
    this.currentPage = 1;
    this.updatePagination();
  }

  clearSearch() {
    this.searchQuery = "";
    this.onSearchChange();
  }

  onItemsPerPageChange() {
    localStorage.setItem('memoriesItemsPerPage', this.itemsPerPage.toString());
    this.currentPage = 1;
    this.updatePagination();
  }

  onSortChange() {
    this.updatePagination();
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  goToFirstPage() {
    this.currentPage = 1;
    this.updatePagination();
    this.scrollToTop();
  }

  goToLastPage() {
    this.currentPage = this.totalPages;
    this.updatePagination();
    this.scrollToTop();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== -1) {
      this.currentPage = page;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  jumpToPage() {
    if (this.jumpToPageNumber >= 1 && this.jumpToPageNumber <= this.totalPages) {
      this.currentPage = this.jumpToPageNumber;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 7;
    
    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 3) {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  getStartIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage;
  }

  getEndIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return Math.min(end, this.filteredMemories.length);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // NEW: View mode methods
  setViewMode(mode: 'grid' | 'list' | 'compact') {
    this.viewMode = mode;
    localStorage.setItem('memoriesViewMode', mode);
  }
}